if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      const submitButton = this.querySelector('[type="submit"]');
      if (submitButton.classList.contains('loading')) return; 

      this.handleErrorMessage();
      this.cartNotification.setActiveElement(document.activeElement);

      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        ...JSON.parse(serializeForm(this.form)),
        sections: this.cartNotification.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname
      });

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.cartNotification.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

class ImageZoom extends HTMLElement {
  static selectors = {
    imageZoom: '[data-image-zoom]'
  };

  static classes = {
    zoomImg: 'zoomImg'
  };

  static attributes = {
    imageZoomTarget: 'data-image-zoom-target'
  };

  constructor() {
    super();

    this.container = this;
    this.cache = {};
    this.url = this.dataset.zoom;

    this._cacheSelectors();
    this._duplicateImage();

    if (!this.cache.sourceImage) return;
  }

  _init() {
    var targetWidth = this.cache.targetImage.width;
    var targetHeight = this.cache.targetImage.height;

    if (this.cache.sourceImage === this.cache.targetImage) {
      this.sourceWidth = targetWidth;
      this.sourceHeight = targetHeight;
    } else {
      this.sourceWidth = this.cache.sourceImage.width;
      this.sourceHeight = this.cache.sourceImage.height;
    }

    this.xRatio =
      (this.cache.sourceImage.width - targetWidth) / this.sourceWidth;
    this.yRatio =
      (this.cache.sourceImage.height - targetHeight) / this.sourceHeight;
  }

  _cacheSelectors() {
    this.cache = {
      sourceImage: this.querySelector(ImageZoom.selectors.imageZoom)
    };
  }

  _start(e) {
    this._init();
    this._move(e);
  }

  _stop() {
    this.cache.targetImage.style.opacity = 0;
  }

  /**
   * Sets the correct coordinates top and left position in px
   * It sets a limit within between 0 and the max height of the image
   * So when the mouse leaves the target image, it could
   * never go above or beyond the target image zone
   */
  _setTopLeftMaxValues(top, left) {
    return {
      left: Math.max(Math.min(left, this.sourceWidth), 0),
      top: Math.max(Math.min(top, this.sourceHeight), 0)
    };
  }

  _move(e) {
    // get left and top position within the "source image" zone
    var left = (e.pageX ? 
      (e.pageX - (this.cache.sourceImage.getBoundingClientRect().left + window.scrollX)) 
      : (e.touches[0].clientX - (this.cache.sourceImage.getBoundingClientRect().left)))
    var top = (e.pageY ? 
      (e.pageY - (this.cache.sourceImage.getBoundingClientRect().top + window.scrollY)) 
      : (e.touches[0].clientY - (this.cache.sourceImage.getBoundingClientRect().top)));
    // make sure the left and top position don't go
    // above or beyond the target image zone
    var position = this._setTopLeftMaxValues(top, left);

    top = position.top;
    left = position.left;

    this.cache.targetImage.style.left = -(left * -this.xRatio) + 'px';
    this.cache.targetImage.style.top = -(top * -this.yRatio) + 'px';
    this.cache.targetImage.style.opacity = 1;

    e.preventDefault();
  }

  /**
   * This loads a high resolution image
   * via the data attributes url
   * It adds all necessary CSS styles and adds to the container
   */
  _duplicateImage() {
    this._loadImage()
      .then(
        function(image) {
          this.cache.targetImage = image;
          image.style.width = image.width + 'px';
          image.style.height = image.height + 'px';
          image.style.position = 'absolute';
          image.style.maxWidth = 'none';
          image.style.maxHeight = 'none';
          image.style.opacity = 0;
          image.style.border = 'none';
          image.style.left = 0;
          image.style.top = 0;

          this.container.appendChild(image);

          this._init();

          this._start = this._start.bind(this);
          this._stop = this._stop.bind(this);
          this._move = this._move.bind(this);

          this.container.addEventListener('mouseenter', this._start);
          this.container.addEventListener('mouseleave', this._stop);
          this.container.addEventListener('mousemove', this._move);
          this.container.addEventListener('touchstart', this._start);
          this.container.addEventListener('touchend', this._stop);
          this.container.addEventListener('touchmove', this._move);

          this.container.style.position = 'relative';
          this.container.style.overflow = 'hidden';
        }.bind(this)
      )
      .catch(function(error) {
        // eslint-disable-next-line no-console
        console.warn('Error fetching image', error);
      });
  }

  _loadImage() {
    // eslint-disable-next-line
    return new Promise(function(resolve, reject) {
        var image = new Image();
        image.setAttribute('role', 'presentation');
        image.setAttribute(ImageZoom.attributes.imageZoomTarget, true);
        image.classList.add(ImageZoom.classes.zoomImg);
        image.src = this.url;

        image.addEventListener('load', function() {
          resolve(image);
        });

        image.addEventListener('error', function(error) {
          reject(error);
        });
      }.bind(this)
    );
  }

  unload() {
    var targetImage = this.container.querySelector(
      '[' + attributes.imageZoomTarget + ']'
    );
    if (targetImage) {
      targetImage.remove();
    }

    this.container.removeEventListener('mouseenter', this._start);
    this.container.removeEventListener('mouseleave', this._stop);
    this.container.removeEventListener('mousemove', this._move);
    this.container.removeEventListener('touchstart', this._start);
    this.container.removeEventListener('touchend', this._stop);
    this.container.removeEventListener('touchmove', this._move);
  }
}

customElements.define('img-zoom', ImageZoom);
